import React, { useMemo, useState } from "react";
import { Container, Row, Col, Form, Card, Badge, Button } from "react-bootstrap";
import { Helmet } from "react-helmet";
import { Link } from "react-router";

import glossaryData from "../data/glossaryData.js";
import Layout from "../components/layout/Layout.js";
import NewsletterForm from "../components/newsletter/NewsletterForm";



export default function Glossary() {

      const [searchTerm, setSearchTerm] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");

  const categories = useMemo(() => {
    const uniqueCategories = [...new Set(glossaryData.map((item) => item.category))];
    return ["All", ...uniqueCategories];
  }, []);

  const filteredTerms = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();

    return glossaryData
      .filter((item) => {
        const matchesCategory =
          activeCategory === "All" || item.category === activeCategory;

        const matchesSearch =
          !query ||
          item.term.toLowerCase().includes(query) ||
          item.shortDefinition.toLowerCase().includes(query) ||
          item.whyItMatters.toLowerCase().includes(query) ||
          item.category.toLowerCase().includes(query);

        return matchesCategory && matchesSearch;
      })
      .sort((a, b) => a.term.localeCompare(b.term));
  }, [searchTerm, activeCategory]);

  const groupedTerms = useMemo(() => {
    return filteredTerms.reduce((acc, item) => {
      const firstLetter = item.term.charAt(0).toUpperCase();
      if (!acc[firstLetter]) {
        acc[firstLetter] = [];
      }
      acc[firstLetter].push(item);
      return acc;
    }, {});
  }, [filteredTerms]);

  const letters = useMemo(() => Object.keys(groupedTerms).sort(), [groupedTerms]);

  return (
    <>
          <Helmet>
        <title>
          Stock Market Glossary for Beginners | Investing Terms Explained | Stocks With Nik
        </title>
        <meta
          name="description"
          content="Learn common stock market and investing terms in plain English. This beginner-friendly glossary from Stocks With Nik explains ETFs, dividends, market cap, P/E ratio, yield, and more."
        />
        <link rel="canonical" href="https://stockswithnik.com/stock-market-glossary" />
      </Helmet>


    <Layout content={
        <>
              <div className="py-5 border-bottom">
        <Container>
          <Row className="justify-content-center">
            <Col lg={10}>
              <div className="text-center">
                <h1 className="mb-3">Stock Market Glossary for Beginners</h1>
                <p className="lead text-muted mb-0">
                  Learn common investing and stock market terms in plain English.
                  This glossary is built to help newer investors better understand
                  what they see, hear, and read when learning about the market.
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      <Container className="py-5">
        <Row className="mb-4 g-3">
          <Col md={7}>
            <Form.Group>
              <Form.Label className="fw-semibold">Search terms</Form.Label>
              <Form.Control
                type="text"
                placeholder="Search ETF, dividend, market cap, yield..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Form.Group>
          </Col>

          <Col md={5}>
            <Form.Group>
              <Form.Label className="fw-semibold">Filter by category</Form.Label>
              <Form.Select
                value={activeCategory}
                onChange={(e) => setActiveCategory(e.target.value)}
              >
                {categories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>

        {letters.length > 0 && (
          <div className="mb-4">
            <div className="d-flex flex-wrap gap-2">
              {letters.map((letter) => (
                <Button
                  key={letter}
                  href={`#section-${letter}`}
                  variant="outline-dark"
                  size="sm"
                >
                  {letter}
                </Button>
              ))}
            </div>
          </div>
        )}

        {filteredTerms.length === 0 ? (
          <div className="text-center py-5">
            <h3 className="mb-2">No matching terms found</h3>
            <p className="text-muted mb-0">
              Try a different keyword or switch the category filter.
            </p>
          </div>
        ) : (
          letters.map((letter) => (
            <section key={letter} id={`section-${letter}`} className="mb-5">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h2 className="mb-0">{letter}</h2>
                <a
                  href="#top"
                  className="small text-decoration-none"
                  onClick={(e) => {
                    e.preventDefault();
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                >
                  Back to top
                </a>
              </div>

              <Row className="g-4">
                {groupedTerms[letter].map((item) => (
                  <Col md={6} lg={4} key={item.slug}>
                    <Card className="h-100 shadow-sm border-0">
                      <Card.Body className="d-flex flex-column">
                        <div className="mb-2">
                          <Badge bg="dark" className="mb-2">
                            {item.category}
                          </Badge>
                          <h3 className="h5 mb-0">{item.term}</h3>
                        </div>

                        <p className="text-muted mb-3">{item.shortDefinition}</p>

                        <div className="mt-auto">
                          <h4 className="h6 fw-bold">Why it matters</h4>
                          <p className="mb-3">{item.whyItMatters}</p>

                          {item.relatedLinks && item.relatedLinks.length > 0 && (
                            <div>
                              <h5 className="h6 fw-bold mb-2">Related</h5>
                              <ul className="mb-0 ps-3">
                                {item.relatedLinks.map((link, index) => (
                                  <li key={`${item.slug}-link-${index}`}>
                                    <a href={link.url} target="_blank" rel="noreferrer">{link.label}</a>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            </section>
          ))
        )}
      </Container>

      <div className="py-5 bg-light border-top">
        <Container>
          <Row className="justify-content-center">
            <Col lg={10}>
              <h2 className="fw-bold mb-4">Frequently Asked Questions</h2>

              <div className="mb-4">
                <h3 className="h5">What are the most important stock market terms for beginners?</h3>
                <p className="mb-0 text-muted">
                  Some of the most important beginner terms include stock, ETF,
                  dividend, market cap, diversification, yield, volatility, and
                  expense ratio.
                </p>
              </div>

              <div className="mb-4">
                <h3 className="h5">What is the difference between a stock and an ETF?</h3>
                <p className="mb-0 text-muted">
                  A stock represents ownership in one company, while an ETF is a
                  fund that can hold many investments inside one product.
                </p>
              </div>

              <div className="mb-4">
                <h3 className="h5">Why should beginners learn investing terms?</h3>
                <p className="mb-0 text-muted">
                  Learning the language of investing helps newer investors feel
                  more confident, understand what they are buying, and make better
                  long-term decisions.
                </p>
              </div>

              <div className="p-4 bg-white rounded shadow-sm border">
                <h3 className="h4 fw-bold">Keep Learning With Stocks With Nik</h3>
                <p className="mb-3 text-muted">
                  Ready to go beyond the glossary? Explore more beginner-friendly
                  content on ETFs, dividends, stock indexes, and portfolio
                  building.
                </p>
                <div className="d-flex flex-wrap gap-2">
                  <Link to="/learn" className="btn btn-dark">
                    Visit the Blog
                  </Link>
                  <a href="https://www.youtube.com/@stockswithnik" className="btn btn-outline-dark" target="_blank" rel="noreferrer">
                    Watch on YouTube
                  </a>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
      {/* CTA */}
            <div className="mt-5 p-3" style={{ textAlign: "center" }}>
                <h3>Not sure what fits you best? </h3>
              <p className="mb-2">
                Take the free AI Portfolio Quiz and
                get a beginner-friendly portfolio built around your goals.
              </p>
              <Link to="/quiz">
                <Button>Build My Portfolio</Button>
              </Link>
              <NewsletterForm />
            </div>
        </>
    } />
    </>
  )
}
